import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Loader } from "@/components/common/Loader";
import StatusBadge from "@/components/common/StatusBadge";
import { DataTable } from "@/components/common/DataTable";
import { eur, num, shortDate } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, Package, Save, Pencil } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { WebshopPanel } from "@/components/WebshopPanel";

const Field = ({ label, value }) => (
  <div><div className="text-[11px] uppercase tracking-wide text-[#6a6d70]">{label}</div>
    <div className="mt-0.5 text-sm text-[#32363a]">{value ?? "—"}</div></div>
);
const Edit = ({ label, value, onChange, type = "text" }) => (
  <div><div className="text-[11px] uppercase tracking-wide text-[#6a6d70]">{label}</div>
    <input type={type} value={value ?? ""} onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
      className="mt-1 w-full rounded-md border border-[#c8cfda] bg-white px-2 py-1.5 text-sm outline-none focus:border-[#F97316]" /></div>
);

export default function MaterialDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [stock, setStock] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});

  const load = () => {
    api.get(`/materials/${encodeURIComponent(code)}`).then((r) => { setM(r.data); setForm(r.data); }).catch(() => setM(false));
    api.get(`/materials/${encodeURIComponent(code)}/stock`).then((r) => setStock(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [code]);

  if (m === null) return <Loader label="Loading material master…" />;
  if (m === false) return <div className="p-8 text-[#6a6d70]">Material not found.</div>;

  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const save = async () => {
    try {
      await api.put(`/materials/${encodeURIComponent(code)}`, {
        name: form.name, type: form.type, uom: form.uom, mat_group: form.mat_group, sub_group: form.sub_group,
        abc_class: form.abc_class, unit_cost: Number(form.unit_cost), reorder_point: Number(form.reorder_point),
        mrp_controller: form.mrp_controller, safety_stock: Number(form.safety_stock),
      });
      toast.success("Material master saved"); setEdit(false); load();
    } catch (e) { toast.error("Save failed"); }
  };

  const binCols = [
    { key: "bin_code", label: "Storage Bin", render: (r) => <span className="font-mono text-xs text-[#EC4899]">{r.bin_code}</span> },
    { key: "zone", label: "Zone", render: (r) => <span className="font-mono">{r.zone}</span> },
    { key: "qty", label: "Qty", render: (r) => <span className="font-mono">{num(r.qty)} {r.uom}</span> },
    { key: "occupancy_pct", label: "Occupancy", render: (r) => <span className="font-mono">{r.occupancy_pct}%</span> },
    { key: "status", label: "Status", badge: true },
  ];
  const mvCols = [
    { key: "timestamp", label: "Date", render: (r) => shortDate(r.timestamp) },
    { key: "movement_type", label: "Mvt", render: (r) => <span className="font-mono text-xs text-[#F97316]">{r.movement_type}</span> },
    { key: "qty", label: "Qty", render: (r) => <span className={`font-mono ${r.qty < 0 ? "text-[#bb0000]" : "text-emerald-600"}`}>{num(r.qty)}</span> },
    { key: "ref", label: "Reference", render: (r) => <span className="font-mono text-xs text-[#6a6d70]">{r.ref}</span> },
  ];
  const V = edit ? Edit : Field;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/materials")} data-testid="mat-back-btn" className="inline-flex items-center gap-1.5 text-sm text-[#0a6ed1] hover:underline"><ArrowLeft size={16} /> Materials</button>

      <div className="rounded-lg border border-[#e5e9f0] bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <Package size={20} className="text-[#F97316]" />
              <h1 className="text-2xl font-semibold tracking-tight text-[#32363a]">{m.name}</h1>
              <StatusBadge status={m.status} />
            </div>
            <p className="mt-1 font-mono text-sm text-[#6a6d70]">{m.material_code} · Plant {m.plant} · {m.type}</p>
          </div>
          {edit
            ? <button data-testid="mat-save-btn" onClick={save} className="inline-flex items-center gap-1.5 rounded-md bg-[#F97316] px-4 py-2 text-sm font-semibold text-white hover:bg-[#ea580c]"><Save size={16} /> Save</button>
            : <button data-testid="mat-edit-btn" onClick={() => setEdit(true)} className="inline-flex items-center gap-1.5 rounded-md border border-[#c8cfda] bg-white px-4 py-2 text-sm font-semibold text-[#32363a] hover:border-[#F97316]"><Pencil size={16} /> Edit</button>}
        </div>
      </div>

      <Tabs defaultValue="basic">
        <TabsList className="flex-wrap bg-white border border-[#e5e9f0]">
          {["basic", "sales", "purchasing", "mrp", "accounting", "stock", "webshop"].map((t) => (
            <TabsTrigger key={t} data-testid={`mat-tab-${t}`} value={t} className="capitalize data-[state=active]:bg-[#eaedf2] data-[state=active]:text-[#F97316]">{t === "mrp" ? "MRP" : t === "stock" ? "Stock Overview" : t}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="basic" className="mt-4"><div className="grid grid-cols-2 gap-5 rounded-lg border border-[#e5e9f0] bg-white p-5 lg:grid-cols-3">
          <Field label="Material" value={m.material_code} />
          <V label="Description" value={form.name} onChange={set("name")} />
          <V label="Material Type" value={form.type} onChange={set("type")} />
          <V label="Base UoM" value={form.uom} onChange={set("uom")} />
          <V label="Material Group" value={form.mat_group} onChange={set("mat_group")} />
          <V label="Sub Group" value={form.sub_group} onChange={set("sub_group")} />
          <Field label="Division" value={m.division} />
          <Field label="Product Hierarchy" value={m.product_hierarchy} />
          <Field label="Gross / Net Weight" value={`${num(m.gross_weight)} / ${num(m.net_weight)} ${m.weight_unit || "KG"}`} />
        </div></TabsContent>
        <TabsContent value="sales" className="mt-4"><div className="grid grid-cols-2 gap-5 rounded-lg border border-[#e5e9f0] bg-white p-5 lg:grid-cols-3">
          <Field label="Sales Org" value={`${m.sales_org || "1000"} Euro Packaging`} />
          <Field label="Distribution Channel" value={`${m.dist_channel || "10"} Direct`} />
          <Field label="Division" value={`${m.division || "00"} Packaging`} />
          <Field label="Sales UoM" value={m.sales_uom || m.uom} />
          <Field label="Tax Classification" value={m.tax_class || "1 (Full 20%)"} />
        </div></TabsContent>
        <TabsContent value="purchasing" className="mt-4"><div className="grid grid-cols-2 gap-5 rounded-lg border border-[#e5e9f0] bg-white p-5 lg:grid-cols-3">
          <Field label="Purchasing Group" value={m.type === "ROH" ? "P01 Raw Materials" : "P02 Trade Goods"} />
          <Field label="Procurement Type" value={m.procurement_type === "F" ? "F External" : "E In-house"} />
          <Field label="Order Unit" value={m.order_uom || m.uom} />
          <Field label="Planned Deliv. Time" value={`${num(m.plnd_deliv_time_days)} days`} />
          <Field label="GR Processing Time" value={`${num(m.gr_processing_days)} days`} />
          <Field label="Fixed Bin" value={m.fixed_bin || "—"} />
          <Field label="Plant" value={m.plant} />
        </div></TabsContent>
        <TabsContent value="mrp" className="mt-4"><div className="grid grid-cols-2 gap-5 rounded-lg border border-[#e5e9f0] bg-white p-5 lg:grid-cols-3">
          <Field label="MRP Type" value={m.mrp_type || "PD"} />
          <V label="MRP Controller" value={form.mrp_controller} onChange={set("mrp_controller")} />
          <V label="Reorder Point" value={form.reorder_point} onChange={set("reorder_point")} type="number" />
          <V label="Safety Stock" value={form.safety_stock} onChange={set("safety_stock")} type="number" />
          <Field label="Lot Size" value={m.lot_size} />
          <Field label="Min Lot Size" value={num(m.min_lot_size)} />
          <Field label="In-house Prod. Time" value={`${num(m.inhouse_prod_days)} days`} />
          <V label="ABC Indicator" value={form.abc_class} onChange={set("abc_class")} />
        </div></TabsContent>
        <TabsContent value="accounting" className="mt-4"><div className="grid grid-cols-2 gap-5 rounded-lg border border-[#e5e9f0] bg-white p-5 lg:grid-cols-3">
          <Field label="Valuation Class" value={m.valuation_class || (m.type === "ROH" ? "3000 Raw" : "7920 Finished")} />
          <Field label="Price Control" value={m.price_control === "S" ? "S Standard" : "V Moving Avg"} />
          <V label="Standard Price" value={form.unit_cost} onChange={set("unit_cost")} type="number" />
          <Field label="Moving Avg Price" value={eur(m.moving_avg_price)} />
          <Field label="Price Unit" value={num(m.price_unit || 1)} />
          <Field label="Currency" value={m.currency} />
          <Field label="Profit Center" value={m.profit_center} />
          <Field label="Stock Value" value={eur(m.stock_value)} />
        </div></TabsContent>
        <TabsContent value="stock" className="mt-4 space-y-5">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <div className="rounded-lg border border-[#e5e9f0] bg-white p-4"><div className="text-[11px] uppercase text-[#6a6d70]">On Hand</div><div className="mt-1 text-xl font-semibold text-[#32363a]">{num(stock?.on_hand)} {m.uom}</div></div>
            <div className="rounded-lg border border-[#e5e9f0] bg-white p-4"><div className="text-[11px] uppercase text-[#6a6d70]">Free Stock</div><div className="mt-1 text-xl font-semibold text-[#32363a]">{num(stock?.free_stock)}</div></div>
            <div className="rounded-lg border border-[#e5e9f0] bg-white p-4" data-testid="mat-committed-card"><div className="text-[11px] uppercase text-[#6a6d70]">Reserved (Web Orders)</div><div className="mt-1 text-xl font-semibold text-amber-600">{num(stock?.committed_web)}</div><div className="mt-0.5 text-[10px] text-[#9aa0a6]">Pending portal orders</div></div>
            <div className="rounded-lg border border-[#e5e9f0] bg-white p-4"><div className="text-[11px] uppercase text-[#6a6d70]">Reorder Point</div><div className="mt-1 text-xl font-semibold text-[#32363a]">{num(stock?.reorder_point)}</div></div>
            <div className="rounded-lg border border-[#e5e9f0] bg-white p-4"><div className="text-[11px] uppercase text-[#6a6d70]">Stock Value</div><div className="mt-1 text-xl font-semibold text-emerald-600">{eur(m.stock_value)}</div></div>
          </div>
          <div><h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">Stock by Storage Bin</h3><DataTable testId="mat-bins-table" columns={binCols} rows={stock?.bins || []} empty="No bin stock." /></div>
          <div><h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">Recent Movements</h3><DataTable testId="mat-moves-table" columns={mvCols} rows={stock?.movements || []} empty="No movements." /></div>
        </TabsContent>
        <TabsContent value="webshop" className="mt-4">
          <WebshopPanel material={m} onSaved={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
